import {
  forwardRef,
  isValidElement,
  ReactNode,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import composeRefs from '@seznam/compose-react-refs';
import { AnimatePresence } from 'motion/react';

import { AnimatedIcon } from '@components/primitives/AnimatedIcon';
import { useFocusRing } from '@components/primitives/FocusRing';
import { useFormField } from '@components/primitives/FormField';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import { CheckboxIcon } from './CheckboxIcon';
import { CheckboxLabel } from './CheckboxLabel';
import { CheckboxContext } from './context';

import css from './styles.module.css';

export type CheckedState = CheckboxPrimitive.CheckedState;

export interface CheckboxProps {
  id?: string;
  name?: string;
  value?: string;
  checked?: CheckedState;
  defaultChecked?: CheckedState;
  onChange?: (checked: boolean) => void;
  children: ReactNode;
  className?: string;
  animated?: boolean;
  disabled?: boolean;
  required?: boolean;
}

/**
 * An element child is rendered as given — the caller has composed it, and
 * usually it is the `Checkbox.Label`. Anything else is bare text, so it gets a
 * label of its own and the common case stays a one-liner.
 */
function withLabel(children: ReactNode): ReactNode {
  return isValidElement(children) ? (
    children
  ) : (
    <CheckboxLabel>{children}</CheckboxLabel>
  );
}

/**
 * Renders the control, then whatever it is given. `Checkbox.Label` associates
 * itself with the control through context, so the label's markup is the
 * caller's to compose.
 */
const CheckboxRoot = forwardRef<HTMLButtonElement, CheckboxProps>(
  function Checkbox(
    {
      children,
      checked: controlledChecked,
      defaultChecked,
      onChange,
      id: propsId,
      name,
      value,
      className,
      animated = true,
      disabled,
      required,
    },
    ref
  ) {
    const form = useFormField();
    const internalId = useId();
    // Deliberately not form.id: this renders its own <label>, so borrowing the
    // FormField id would double the accessible name and collide across siblings.
    const id = propsId ?? internalId;

    const [buttonRef, focusStyle] = useFocusRing<HTMLButtonElement>();

    const [uncontrolledChecked, setUncontrolledChecked] =
      useState<CheckedState>(defaultChecked ?? false);
    const isControlled = controlledChecked !== undefined;
    const checked = isControlled ? controlledChecked : uncontrolledChecked;

    const handleCheckedChange = useCallback(
      (next: CheckedState) => {
        if (!isControlled) setUncontrolledChecked(next);
        onChange?.(next === true);
      },
      [isControlled, onChange]
    );

    const context = useMemo(() => ({ id }), [id]);

    return (
      <CheckboxContext.Provider value={context}>
        <div className={cn(css.container, className)}>
          <CheckboxPrimitive.Root
            ref={composeRefs(ref, buttonRef)}
            id={id}
            name={name}
            value={value}
            checked={checked}
            onCheckedChange={handleCheckedChange}
            disabled={disabled}
            required={required}
            aria-invalid={form?.error ? true : undefined}
            className={css.checkbox}
            style={focusStyle}
          >
            <CheckboxPrimitive.Indicator forceMount asChild>
              <span className={css.checkboxInner} aria-hidden>
                <span className={css.checkboxIcon}>
                  <AnimatePresence initial={false}>
                    {checked === true && (
                      <AnimatedIcon.Check
                        initial={animated ? 'from' : 'to'}
                        animate="to"
                        transition={{
                          delay: 0.05,
                          duration: 0.2,
                          ease: easeOutSmooth,
                        }}
                      />
                    )}

                    {checked === 'indeterminate' && (
                      <span className={css.indeterminateIcon} />
                    )}
                  </AnimatePresence>
                </span>
              </span>
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>

          {withLabel(children)}
        </div>
      </CheckboxContext.Provider>
    );
  }
);

export const Checkbox = Object.assign(CheckboxRoot, {
  Label: CheckboxLabel,
  Icon: CheckboxIcon,
});

export type { CheckboxIconProps } from './CheckboxIcon';
export type { CheckboxLabelProps } from './CheckboxLabel';
export * from './Group';
